import * as THREE from "three";

import { NODATA_COLOR } from "./indicatorEngine";

/**
 * Couche "communes" : donnees communales drapees sur le relief.
 *
 * Partage entre le viewer de production (TerrainViewer) et le bac a sable
 * (terrain_test/TerrainTestPage) : les deux construisent la meme couche a
 * partir des memes fichiers, seule l'UI autour differe.
 *
 * Donnees produites par api/experiments/terrain3d/build_overlays.py :
 *   - communes.bin  : Uint16, une valeur par cellule de la grille du terrain
 *                     (index de commune, ou COMMUNE_NODATA), donc alignee
 *                     cellule a cellule sur heightmap.bin/meta.json.
 *   - communes.json : { indicators, default_indicator, communes } — une
 *                     entree par commune (index = valeur du raster), aplatie
 *                     en chemins pointes, ET la description de ce qui est
 *                     cartographiable sur ce territoire.
 *
 * CE MODULE NE CONNAIT PLUS LES INDICATEURS. Il ne sait que draper un raster
 * d'index sur le relief et en tracer les limites ; quels indicateurs
 * existent, comment les colorer et les formater est decrit par le datapack
 * et rendu par indicatorEngine.js. La couche geologique, dont le raster a
 * exactement la meme forme, reutilise ces memes fonctions.
 *
 * Un SEUL raster sert tous les indicateurs : changer d'indicateur ne fait
 * que recalculer un tableau de couleurs (335 couleurs, puis un lookup par
 * cellule), jamais retelecharger ou reconstruire une geometrie.
 */

export const COMMUNE_NODATA = 65535;

// Decollage des deux nappes au-dessus du terrain, en unites de scene et
// APRES exageration (meme convention que la pluviometrie a +25 et la
// geologie a +18) : la nappe de couleur passe au-dessus d'elles, et le
// trace des limites au-dessus de la nappe.
export const COMMUNE_CHOROPLETH_Y_OFFSET = 32;
export const COMMUNE_BORDER_Y_OFFSET = 52;

// Opacite de la nappe de couleur. Elle n'est pas eclairee (MeshBasicMaterial,
// comme les autres nappes drapees) : plus elle est opaque, plus elle efface
// l'ombrage du relief qui est dessous, et la carte "s'aplatit". D'ou une
// valeur de depart franchement transparente — assez pour lire l'indicateur,
// assez pour garder le modele du terrain — et un reglage pour trancher au
// cas par cas (comparer des communes vs. montrer le relief).
export const COMMUNE_DEFAULT_OPACITY = 0.55;
export const COMMUNE_MIN_OPACITY = 0.1;
export const COMMUNE_MAX_OPACITY = 1;

// Eclaircissement de la commune survolee : la teinte de l'indicateur reste
// reconnaissable (on la melange vers le blanc plutot que de la remplacer).
const HOVER_MIX = 0.45;
const HOVER_TARGET = new THREE.Color("#ffffff");

export function communeHoverColor(baseColor) {
  return baseColor.clone().lerp(HOVER_TARGET, HOVER_MIX);
}

/**
 * Remplit un attribut `color` (3 floats par cellule) depuis un raster
 * d'index. Commun aux communes et a la geologie : les deux rasters ont la
 * meme forme (Uint16, un index d'entite par cellule de la grille du terrain).
 */
export function paintRasterColors(target, codes, colors) {
  const fallback = new THREE.Color(NODATA_COLOR);
  for (let i = 0; i < codes.length; i++) {
    const color = colors[codes[i]] || fallback;
    target[i * 3] = color.r;
    target[i * 3 + 1] = color.g;
    target[i * 3 + 2] = color.b;
  }
}

/**
 * Cellules de chaque commune (Map<indexCommune, Int32Array>), calculees une
 * fois au chargement : eclaircir la commune survolee revient alors a
 * reecrire ses seules cellules, sans reparcourir les 375 000 du raster a
 * chaque deplacement du curseur.
 */
export function buildCommuneCellIndex(codes) {
  const counts = new Map();
  for (let i = 0; i < codes.length; i++) {
    const id = codes[i];
    if (id === COMMUNE_NODATA) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  const cells = new Map();
  counts.forEach((count, id) => cells.set(id, new Int32Array(count)));
  const filled = new Map();
  for (let i = 0; i < codes.length; i++) {
    const id = codes[i];
    if (id === COMMUNE_NODATA) continue;
    const n = filled.get(id) || 0;
    cells.get(id)[n] = i;
    filled.set(id, n + 1);
  }
  return cells;
}

/** Ecrit une couleur unique sur un jeu de cellules (survol d'une commune). */
export function paintCells(target, cells, color) {
  if (!cells) return;
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    target[c * 3] = color.r;
    target[c * 3 + 1] = color.g;
    target[c * 3 + 2] = color.b;
  }
}

function makeRawHSampler(heights, cols, rows, zmin, nodata) {
  // (fr, fc) = coordonnees FRACTIONNAIRES en indices de grille : les limites
  // communales passent au milieu des cellules (r+0.5), d'ou l'interpolation.
  return (fr, fc) => {
    const r0 = Math.floor(fr);
    const c0 = Math.floor(fc);
    if (r0 < 0 || c0 < 0 || r0 >= rows - 1 || c0 >= cols - 1) {
      const r = Math.min(rows - 1, Math.max(0, Math.round(fr)));
      const c = Math.min(cols - 1, Math.max(0, Math.round(fc)));
      const h = heights[r * cols + c];
      return h === nodata ? null : h - zmin;
    }
    const h00 = heights[r0 * cols + c0];
    const h10 = heights[r0 * cols + c0 + 1];
    const h01 = heights[(r0 + 1) * cols + c0];
    const h11 = heights[(r0 + 1) * cols + c0 + 1];
    if (h00 === nodata || h10 === nodata || h01 === nodata || h11 === nodata) return null;
    const fx = fc - c0;
    const fy = fr - r0;
    return h00 * (1 - fx) * (1 - fy) + h10 * fx * (1 - fy) + h01 * (1 - fx) * fy + h11 * fx * fy - zmin;
  };
}

/**
 * Limites communales, deduites du raster (une arete la ou deux cellules
 * voisines n'appartiennent pas a la meme commune) : aucun fichier de
 * contours a generer/telecharger en plus.
 *
 * Les segments sont GROUPES PAR COMMUNE et chaque limite est ecrite dans les
 * deux communes qu'elle separe. Le trace complet se dessine d'un bloc, et
 * surligner une commune au survol revient a un setDrawRange sur sa tranche —
 * aucune geometrie construite a la volee pendant le survol.
 *
 * Retourne { positions, rawH, ranges: Map<indexCommune, {start, count}> }.
 * Y est laisse a 0 : c'est applyDrapedExaggeration qui le pose.
 */
export function buildCommuneBorders({ codes, heights, cols, rows, cellsize, zmin, nodata }) {
  const sampleRawH = makeRawHSampler(heights, cols, rows, zmin, nodata);
  const halfW = ((cols - 1) * cellsize) / 2;
  const halfH = ((rows - 1) * cellsize) / 2;

  const perCommune = new Map(); // index de commune -> [[x0,z0,h0,x1,z1,h1], ...]
  const addBorder = (idA, idB, fr0, fc0, fr1, fc1) => {
    const h0 = sampleRawH(fr0, fc0);
    const h1 = sampleRawH(fr1, fc1);
    if (h0 == null || h1 == null) return;
    const segment = [
      fc0 * cellsize - halfW, fr0 * cellsize - halfH, h0,
      fc1 * cellsize - halfW, fr1 * cellsize - halfH, h1,
    ];
    [idA, idB].forEach((id) => {
      if (id === COMMUNE_NODATA) return;
      const list = perCommune.get(id);
      if (list) list.push(segment);
      else perCommune.set(id, [segment]);
    });
  };

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols - 1; c++) {
      const a = codes[r * cols + c];
      const b = codes[r * cols + c + 1];
      if (a !== b) addBorder(a, b, r - 0.5, c + 0.5, r + 0.5, c + 0.5);
    }
  }
  for (let r = 0; r < rows - 1; r++) {
    for (let c = 0; c < cols; c++) {
      const a = codes[r * cols + c];
      const b = codes[(r + 1) * cols + c];
      if (a !== b) addBorder(a, b, r + 0.5, c - 0.5, r + 0.5, c + 0.5);
    }
  }

  let totalSegments = 0;
  perCommune.forEach((segments) => {
    totalSegments += segments.length;
  });

  const positions = new Float32Array(totalSegments * 6);
  const rawH = new Float32Array(totalSegments * 2);
  const ranges = new Map();
  let vertex = 0;
  perCommune.forEach((segments, id) => {
    const start = vertex;
    segments.forEach((s) => {
      positions[vertex * 3] = s[0];
      positions[vertex * 3 + 2] = s[1];
      rawH[vertex] = s[2];
      vertex++;
      positions[vertex * 3] = s[3];
      positions[vertex * 3 + 2] = s[4];
      rawH[vertex] = s[5];
      vertex++;
    });
    ranges.set(id, { start, count: vertex - start });
  });

  return { positions, rawH, ranges };
}

/**
 * Pose la coordonnee Y d'une geometrie drapee (limites communales) a partir
 * des altitudes brutes memorisees : seul Y depend de l'exageration, X/Z ne
 * sont jamais recalcules. `offsetWorld` decolle le trace du terrain pour
 * eviter le z-fighting — applique APRES l'exageration, comme les autres
 * nappes drapees (pluviometrie, geologie), pour que l'ecart a l'ecran ne
 * change pas avec le relief.
 */
export function applyDrapedExaggeration(geometry, rawH, exaggeration, offsetWorld) {
  const position = geometry.attributes.position;
  for (let i = 0; i < rawH.length; i++) {
    position.setY(i, rawH[i] * exaggeration + offsetWorld);
  }
  position.needsUpdate = true;
}

/**
 * Cherche la cellule de terrain visee par un rayon, par marche le long du
 * rayon contre le modele d'altitude puis dichotomie.
 *
 * Pourquoi pas un raycast three.js contre le maillage terrain : celui-ci
 * fait des centaines de milliers de triangles et serait teste un par un a
 * chaque mouvement de souris. Pourquoi pas non plus l'astuce du plan
 * horizontal utilisee pour les rivieres : elle ne vaut que pres du niveau
 * des cours d'eau, alors qu'une commune de crete est a plus de 1 500 m —
 * le point vise serait attribue a la mauvaise commune.
 *
 * Retourne { x, z, col, row, index } (index = position dans le raster) ou null.
 */
export function createTerrainPicker({ heights, cols, rows, cellsize, zmin, zmax, nodata }) {
  const halfW = ((cols - 1) * cellsize) / 2;
  const halfH = ((rows - 1) * cellsize) / 2;
  const maxRawH = zmax - zmin;
  const step = cellsize * 0.5;
  const MAX_STEPS = 4000;

  const rawHAtLocal = (x, z) => {
    const fc = (x + halfW) / cellsize;
    const fr = (z + halfH) / cellsize;
    const c0 = Math.floor(fc);
    const r0 = Math.floor(fr);
    if (c0 < 0 || r0 < 0 || c0 >= cols - 1 || r0 >= rows - 1) return null;
    const h00 = heights[r0 * cols + c0];
    const h10 = heights[r0 * cols + c0 + 1];
    const h01 = heights[(r0 + 1) * cols + c0];
    const h11 = heights[(r0 + 1) * cols + c0 + 1];
    if (h00 === nodata || h10 === nodata || h01 === nodata || h11 === nodata) return null;
    const fx = fc - c0;
    const fy = fr - r0;
    return h00 * (1 - fx) * (1 - fy) + h10 * fx * (1 - fy) + h01 * (1 - fx) * fy + h11 * fx * fy - zmin;
  };

  return function pickTerrainCell(ray, exaggeration) {
    const o = ray.origin;
    const d = ray.direction;
    // Intervalle de parcours utile : la portion du rayon qui traverse la
    // boite englobante du terrain. Sans ca, on marcherait sur toute la
    // profondeur de la scene pour rien.
    let tMin = 0;
    let tMax = Infinity;
    const clipSlab = (lo, hi, origin, direction) => {
      if (Math.abs(direction) < 1e-9) return origin >= lo && origin <= hi;
      let ta = (lo - origin) / direction;
      let tb = (hi - origin) / direction;
      if (ta > tb) [ta, tb] = [tb, ta];
      tMin = Math.max(tMin, ta);
      tMax = Math.min(tMax, tb);
      return tMax >= tMin;
    };
    if (!clipSlab(-halfW, halfW, o.x, d.x)) return null;
    if (!clipSlab(-halfH, halfH, o.z, d.z)) return null;
    if (!clipSlab(0, maxRawH * exaggeration, o.y, d.y)) return null;

    const heightAbove = (t) => {
      const h = rawHAtLocal(o.x + d.x * t, o.z + d.z * t);
      return h == null ? null : o.y + d.y * t - h * exaggeration;
    };

    let tAbove = null;
    let t = tMin;
    for (let i = 0; i <= MAX_STEPS && t <= tMax; i++) {
      const diff = heightAbove(t);
      if (diff != null) {
        if (diff <= 0) {
          if (tAbove == null) break; // le rayon demarre deja sous le relief : point non fiable
          let lo = tAbove;
          let hi = t;
          for (let k = 0; k < 14; k++) {
            const mid = (lo + hi) / 2;
            const midDiff = heightAbove(mid);
            if (midDiff == null || midDiff > 0) lo = mid;
            else hi = mid;
          }
          const x = o.x + d.x * hi;
          const z = o.z + d.z * hi;
          const col = Math.min(cols - 1, Math.max(0, Math.round((x + halfW) / cellsize)));
          const row = Math.min(rows - 1, Math.max(0, Math.round((z + halfH) / cellsize)));
          return { x, z, col, row, index: row * cols + col };
        }
        tAbove = t;
      }
      t = t === tMax ? tMax + step : Math.min(t + step, tMax);
    }
    return null;
  };
}
